import { formatNumber, number, t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import { Ban, ChevronRight, Search, ShieldAlert, Users } from 'lucide-react';
import { useEffect, useRef } from 'react';

// lucide-react v1 ships no brand logos, so the social glyphs are inlined here
// (simple-icons paths, currentColor fill) to preserve the brand look.
const BrandIcon = ({ size, path, label }: { size: number; path: string; label: string }) => {
  useLocale();
  return (
    <svg
      aria-label={label}
      fill="currentColor"
      height={size}
      role="img"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={path} />
    </svg>
  );
};
const Linkedin = ({ size }: { size: number }) => {
  useLocale();
  return (
    <BrandIcon
      label={t('landing.linkedin')}
      path="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"
      size={size}
    />
  );
};
const Instagram = ({ size }: { size: number }) => {
  useLocale();
  return (
    <BrandIcon
      label={t('landing.instagram')}
      path="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
      size={size}
    />
  );
};
const XLogo = ({ size }: { size: number }) => {
  useLocale();
  return (
    <BrandIcon
      label="X"
      path="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
      size={size}
    />
  );
};

/**
 * "Your Compliance Report Is Ready" — a staged report build-up animation.
 *
 * Ported from the standalone landinghtml/report-section.html prototype.
 * Adaptations for the landing page:
 *  - All `body.mode-*` selectors are rewritten to a component-scoped
 *    `.report-root.mode-*` so the staging cannot leak onto the page <body>.
 *  - Material Symbols / Font Awesome glyphs are replaced with lucide-react
 *    icons (those icon fonts are not loaded in the app).
 *  - The build sequence is triggered the first time the section scrolls into
 *    view rather than on page load.
 *
 * The phase sequence (outline → overview → nocolor+ai-reveal → colored) and the
 * canvas donut animation are driven imperatively in a single mount effect.
 */

/** Timing knobs for the report build sequence — adjust delays/durations here. */
const REPORT_ANIMATION_TIMING = {
  // Imperative phase transitions (ms from sequence start)
  phaseOutlineToOverviewMs: 0,
  phaseOverviewToAiRevealMs: 0,
  phaseAiRevealToColoredMs: 5000,

  // Canvas donut animation (ms)
  donutBuildDurationMs: 3200,
  donutColorizeDurationMs: 4500,

  // CSS transition & animation durations (seconds)
  transitionColorS: 1,
  transitionOpacityS: 1,
  floatInS: 1.2,
  fadeRevealS: 1.8,
  typeTitleS: 3.8,
  gradientFlowS: 2.4,
  scannerSweepS: 1.8,
  readyFlashS: 1.8,
  overlayFadeOutS: 1.2,

  // Staggered card-child reveal delays (seconds)
  revealChildDelaysS: [0.4, 0.9, 1.4, 1.9, 2.4],
} as const;

const revealChildDelayRules = REPORT_ANIMATION_TIMING.revealChildDelaysS
  .map(
    (delayS, index) =>
      `.report-root.mode-nocolor.ai-reveal .card > *:nth-child(${index + 1}) { animation-delay: ${delayS}s; }`,
  )
  .join('\n');

// Scoped styles. Everything is nested under `.report-root`; `body.mode-*` from
// the prototype becomes `.report-root.mode-*`.
const SCOPED_CSS = `
.report-root {
  --r-bg: #F2F4FD;
  --r-card: #FFFFFF;
  --r-blue: #223a98;
  --r-text: #604C67;
  --r-border: #DED6E0;
  color: #594926;
  font-family: 'Montserrat', sans-serif;
}
.report-root *, .report-root *::before, .report-root *::after { box-sizing: border-box; }

.report-root .container { width: 1010px; max-width: calc(100vw - 120px); margin: 0 auto; }

.report-root .hero { text-align: center; margin-bottom: 100px; }
.report-root .hero h1 {
  font-family: 'Libre Baskerville', serif;
  font-size: 42px;
  margin: 0;
  background: linear-gradient(90deg, #2140c4, #b98bc7);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.report-root .report-icon { display: inline-flex; vertical-align: middle; line-height: 1; }

.report-root .tag {
  display: inline-block;
  background: #ece7ef;
  padding: 7px 14px;
  border-radius: 20px;
  font-size: 12px;
  margin-bottom: 10px;
  color: #666;
}

.report-root .grid { display: grid; grid-template-columns: 1fr 1.65fr; gap: 12px; }

.report-root .card {
  background: var(--r-card);
  border: 1px solid #DED6E0;
  border-radius: 8px;
  padding: 28px;
}
.report-root .card h2 {
  font-family: 'Montserrat', sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: #13226C;
  margin-bottom: 12px;
}
.report-root .card h3 { font-size: 18px; }

.report-root .profile-top { display: flex; gap: 16px; align-items: center; margin-bottom: 20px; }
.report-root .profile-top h2 { font-size: 24px; font-weight: 500; line-height: 32px; color: #13226C; margin: 0; }
.report-root .avatar {
  width: 60px; height: 60px; border-radius: 50%;
  background: #e4e1e7; display: flex; align-items: center; justify-content: center; color: #666;
}
.report-root .badge { background: #d8ceb0; padding: 4px 10px; border-radius: 20px; font-size: 12px; }

.report-root .info-row {
  display: flex; justify-content: space-between; margin: 10px 0;
  font-size: 14px; color: #443649;
}

.report-root .pills { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 15px; }
.report-root .pill {
  display: flex; align-items: center; gap: 8px;
  border-radius: 20px; padding: 7px 12px; font-size: 13px; line-height: 20px;
}
.report-root .pill-pink { background: #FFF7F7; border: 1px solid #D6A9BA; color: #8B6A78; }
.report-root .pill-gold { background: #FFFDF5; border: 1px solid #D9C9A6; color: #8C7A4F; }

.report-root .section-title { font-size: 24px; font-weight: 500; line-height: 24px; color: #13226C; margin: 24px 0 16px; }
.report-root .section-sub-title { font-size: 18px; font-weight: 500; line-height: 32px; color: #13226C; margin: 24px 0 16px; }

.report-root .just-grid { display: grid; grid-template-columns: 1.4fr .9fr; gap: 12px; }

.report-root .donut-wrap { display: flex; align-items: center; padding-top: 15px; }
.report-root .donut-canvas-wrap { position: relative; width: 180px; height: 180px; flex-shrink: 0; }
.report-root #riskDonutCanvas { width: 180px; height: 180px; display: block; }
.report-root .donut-hole { position: absolute; inset: 38px; background: var(--r-card); border-radius: 50%; pointer-events: none; }

.report-root .legend { width: 300px; margin-left: auto; }
.report-root .legend-row { display: flex; justify-content: space-between; align-items: center; margin: 4px 0; font-size: 13px; color: #443649; }
.report-root .legend-label { display: flex; align-items: center; }
.report-root .legend-label span { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 8px; flex-shrink: 0; }
.report-root .legend-value { text-align: right; min-width: 40px; }

.report-root .risk-caption { margin-top: 24px; text-align: center; font-size: 14px; font-weight: 400; line-height: 24px; color: #443649; }
.report-root .risk-level-text { color: #AB6400; font-weight: 500; }

.report-root .bullet { margin: 18px 0; color: #443649; line-height: 1.5; }
.report-root .bullet:before { content: "◉ "; color: #A18445; }

.report-root .signals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.report-root .signal-card {
  background: var(--r-card); border-radius: 8px; padding: 18px; border: 1px solid #d8cfd6;
  min-height: 100px; display: flex; flex-direction: column;
}
.report-root .signal-header { display: flex; justify-content: space-between; align-items: center; color: #604C67; margin-bottom: 16px; }
.report-root .signal-card h4 { color: #604C67; font-size: 13px; margin: 0; font-weight: 400; }
.report-root .signal-content { display: flex; align-items: flex-start; gap: 12px; min-height: 110px; }
.report-root .signal-content .num { flex-shrink: 0; font-size: 36px; font-weight: 600; line-height: 40px; color: #13226C; }
.report-root .signal-card .desc { font-size: 14px; font-weight: 500; line-height: 24px; color: #13226C; margin-top: 6px; }
.report-root .signal-footer { margin-top: auto; display: flex; flex-direction: column; }
.report-root .signal-card .foot { font-size: 12px; color: #604C67; margin-top: 0; }
.report-root .signal-card .more { text-align: right; margin-top: 20px; display: flex; justify-content: flex-end; gap: 4px; align-items: center; }

.report-root .social-card { margin-top: 12px; }
.report-root .social-header { font-size: 14px; color: #443649; margin-bottom: 8px; }
.report-root .social-main { font-size: 24px; line-height: 32px; color: #13226C; font-weight: 500; margin-bottom: 32px; }
.report-root .social-content { display: flex; align-items: flex-start; gap: 12px; }
.report-root .social-content .num { flex-shrink: 0; font-size: 36px; font-weight: 600; line-height: 40px; color: #13226C; }
.report-root .social-content .desc { margin-top: 8px; font-size: 19px; line-height: 24px; font-weight: 600; margin-bottom: 24px; color: #13226C; }
.report-root .social-item { margin-top: 14px; border: 1px solid #ddd; border-radius: 8px; padding: 18px; }
.report-root .social-item h5 { font-size: 16px; font-weight: 500; line-height: 32px; color: #13226C; margin: 0; }
.report-root .social-item p { font-size: 13px; color: #443649; display: flex; align-items: center; gap: 12px; margin: 0; }
.report-root .more { text-align: right; margin-top: 32px; color: #5164ba; font-size: 13px; display: flex; justify-content: flex-end; align-items: center; gap: 4px; }

.report-root p { font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 400; line-height: 24px; color: #443649; margin: 0; }

/* ── View modes ─────────────────────────────────────────────────── */
.report-root.mode-outline .tag,
.report-root.mode-outline .hero h1,
.report-root.mode-outline .card > *,
.report-root.mode-outline .section-title,
.report-root.mode-outline .section-sub-title { visibility: hidden; }
.report-root.mode-outline .card { background: transparent !important; border-color: #7B6184 !important; min-height: 120px; }
.report-root.mode-outline .tag { border: 1px solid #7B6184; color: transparent; background: transparent; }
.report-root.mode-outline .signal-card > *,
.report-root.mode-outline .social-card > * { visibility: hidden; }
.report-root.mode-outline .signal-card { background: transparent !important; border-color: #7B6184 !important; }

.report-root.mode-overview .card { background: #F8F6F8 !important; border-color: #7B6184 !important; }
.report-root.mode-overview .card > *,
.report-root.mode-overview .section-title,
.report-root.mode-overview .hero h1 { visibility: hidden; }
.report-root.mode-overview .tag { background: #F8F6F8; border: 1px solid #7B6184; color: #7B6184; }
.report-root.mode-overview .signal-card { background: #F8F6F8 !important; border-color: #7B6184 !important; }
.report-root.mode-overview .signal-card * { visibility: hidden !important; }

.report-root.mode-nocolor { --r-card: #F8F6F8; --r-blue: #7B6184; --r-text: #7B6184; }
.report-root.mode-nocolor .card,
.report-root.mode-nocolor .signal-card,
.report-root.mode-nocolor .social-item { background: #F8F6F8 !important; border-color: #7B6184 !important; }
.report-root.mode-nocolor .hero h1,
.report-root.mode-nocolor .card h2,
.report-root.mode-nocolor .section-title,
.report-root.mode-nocolor .section-sub-title,
.report-root.mode-nocolor .profile-top h2 {
  background: none !important; -webkit-text-fill-color: #7B6184 !important; color: #7B6184 !important;
}
.report-root.mode-nocolor .badge { background: #DED6E0 !important; color: #7B6184 !important; }
.report-root.mode-nocolor .pill,
.report-root.mode-nocolor .pill-pink,
.report-root.mode-nocolor .pill-gold {
  background: #DED6E0 !important; border: 1px solid #7B6184 !important; color: #7B6184 !important;
}
.report-root.mode-nocolor .bullet,
.report-root.mode-nocolor .bullet:before,
.report-root.mode-nocolor .risk-level-text,
.report-root.mode-nocolor p,
.report-root.mode-nocolor .info-row,
.report-root.mode-nocolor .legend,
.report-root.mode-nocolor .legend-row,
.report-root.mode-nocolor .legend-value,
.report-root.mode-nocolor .social-header,
.report-root.mode-nocolor .social-item p,
.report-root.mode-nocolor .social-item h5,
.report-root.mode-nocolor .more { color: #7B6184 !important; }
.report-root.mode-nocolor .signal-card,
.report-root.mode-nocolor .signal-card *,
.report-root.mode-nocolor .social-content .desc,
.report-root.mode-nocolor .social-content .num,
.report-root.mode-nocolor .signal-content .desc,
.report-root.mode-nocolor .signal-content .num,
.report-root.mode-nocolor .signal-header,
.report-root.mode-nocolor .signal-header h4,
.report-root.mode-nocolor .signal-footer,
.report-root.mode-nocolor .signal-footer * { color: #7B6184 !important; }
.report-root.mode-nocolor .legend-row:nth-child(1) .legend-label span { background: #7B6184 !important; }
.report-root.mode-nocolor .legend-row:nth-child(2) .legend-label span { background: #957B9E !important; }
.report-root.mode-nocolor .legend-row:nth-child(3) .legend-label span { background: #AC98B3 !important; }
.report-root.mode-nocolor .legend-row:nth-child(4) .legend-label span { background: #C4B6C9 !important; }
.report-root.mode-nocolor .legend-row:nth-child(5) .legend-label span { background: #DED6E0 !important; }
.report-root.mode-nocolor *, .report-root.mode-nocolor *:before, .report-root.mode-nocolor *:after { transition: none !important; }
.report-root.mode-nocolor .report-icon { color: #7B6184 !important; }

/* Build-phase transitions */
.report-build-animation * {
  transition:
    background-color ${REPORT_ANIMATION_TIMING.transitionColorS}s cubic-bezier(.22, .61, .36, 1),
    border-color ${REPORT_ANIMATION_TIMING.transitionColorS}s cubic-bezier(.22, .61, .36, 1),
    color ${REPORT_ANIMATION_TIMING.transitionColorS}s cubic-bezier(.22, .61, .36, 1),
    opacity ${REPORT_ANIMATION_TIMING.transitionOpacityS}s cubic-bezier(.22, .61, .36, 1),
    visibility 0s linear,
    transform ${REPORT_ANIMATION_TIMING.transitionOpacityS}s cubic-bezier(.22, .61, .36, 1);
}
.report-root .hero,
.report-root .grid,
.report-root .just-grid,
.report-root .signals,
.report-root .social-card { animation: reportFloatIn ${REPORT_ANIMATION_TIMING.floatInS}s ease both; }
@keyframes reportFloatIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* AI build phase — gradual reveal */
.report-root.mode-nocolor.ai-reveal .card > *,
.report-root.mode-nocolor.ai-reveal .signal-card > *,
.report-root.mode-nocolor.ai-reveal .social-card > *,
.report-root.mode-nocolor.ai-reveal .section-title,
.report-root.mode-nocolor.ai-reveal .hero h1 {
  animation: reportFadeReveal ${REPORT_ANIMATION_TIMING.fadeRevealS}s cubic-bezier(.22, .61, .36, 1) forwards; opacity: 0;
}
${revealChildDelayRules}
.report-root.mode-nocolor.ai-reveal p,
.report-root.mode-nocolor.ai-reveal .bullet,
.report-root.mode-nocolor.ai-reveal .desc,
.report-root.mode-nocolor.ai-reveal .info-row,
.report-root.mode-nocolor.ai-reveal .legend-row,
.report-root.mode-nocolor.ai-reveal .social-item,
.report-root.mode-nocolor.ai-reveal .pill {
  opacity: 0; animation: reportFadeReveal ${REPORT_ANIMATION_TIMING.fadeRevealS}s cubic-bezier(.22, .61, .36, 1) forwards;
}
.report-root.mode-nocolor.ai-reveal .type-title {
  visibility: visible !important; opacity: 1 !important;
  overflow: hidden; white-space: nowrap; width: 0;
  transform: none !important; animation: reportTypeTitle ${REPORT_ANIMATION_TIMING.typeTitleS}s steps(60, end) forwards;
}
.report-root.mode-nocolor.ai-reveal .tag,
.report-root.mode-nocolor.ai-reveal .tag.type-title {
  display: inline-block; width: auto !important; max-width: none !important;
  animation: none !important; opacity: 1 !important; visibility: visible !important; transform: none !important;
  background: #F8F6F8 !important; border: 1px solid #7B6184 !important; color: #7B6184 !important; -webkit-text-fill-color: #7B6184 !important;
}
.report-root.mode-nocolor.ai-reveal .section-title.type-title,
.report-root.mode-nocolor.ai-reveal h1.type-title,
.report-root.mode-nocolor.ai-reveal h2.type-title { display: block; }
@keyframes reportTypeTitle { from { max-width: 0; width: auto; } to { max-width: 1000px; width: auto; } }
@keyframes reportFadeReveal { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

/* Premium AI generation phase — blur + shimmer overlay */
.report-root.mode-nocolor.ai-reveal .card,
.report-root.mode-nocolor.ai-reveal .signal-card,
.report-root.mode-nocolor.ai-reveal .social-card { position: relative; overflow: hidden; }
.report-root.mode-nocolor.ai-reveal .card::after,
.report-root.mode-nocolor.ai-reveal .signal-card::after,
.report-root.mode-nocolor.ai-reveal .social-card::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
  mask-image: linear-gradient(to bottom, rgba(0,0,0,.25) 0%, rgba(0,0,0,.45) 25%, rgba(0,0,0,.65) 50%, rgba(0,0,0,.85) 75%, rgba(0,0,0,1) 100%);
  background: linear-gradient(90deg, rgba(184,192,235,0) 0%, rgba(184,192,235,.12) 20%, rgba(156,130,166,.18) 40%, rgba(216,200,161,.14) 60%, rgba(141,138,195,.18) 80%, rgba(184,192,235,0) 100%);
  background-size: 250% 100%; z-index: 5;
  animation: reportGradientFlow ${REPORT_ANIMATION_TIMING.gradientFlowS}s linear infinite;
}
.report-root.mode-nocolor.ai-reveal .card::before,
.report-root.mode-nocolor.ai-reveal .signal-card::before,
.report-root.mode-nocolor.ai-reveal .social-card::before {
  content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .06;
  background-image:
    repeating-radial-gradient(circle at 20% 20%, rgba(123,97,132,.35) 0 1px, transparent 1px 3px),
    repeating-radial-gradient(circle at 80% 40%, rgba(123,97,132,.25) 0 1px, transparent 1px 4px);
  mix-blend-mode: multiply; z-index: 6;
}
.report-root.mode-nocolor.ai-reveal .card > *,
.report-root.mode-nocolor.ai-reveal .signal-card > *,
.report-root.mode-nocolor.ai-reveal .social-card > * { position: relative; z-index: 2; }
@keyframes reportGradientFlow { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* Reveal clean content at completion */
.report-root.mode-colored .card::before,
.report-root.mode-colored .card::after,
.report-root.mode-colored .signal-card::before,
.report-root.mode-colored .signal-card::after,
.report-root.mode-colored .social-card::before,
.report-root.mode-colored .social-card::after { opacity: 0; transition: opacity ${REPORT_ANIMATION_TIMING.overlayFadeOutS}s ease; }

/* Cinematic finalization scanner sweep (scoped to the section) */
.report-root.mode-colored { position: relative; }
.report-root.mode-colored::after {
  content: ""; position: absolute; inset: -20%; pointer-events: none;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.08) 40%, rgba(255,255,255,.75) 50%, rgba(255,255,255,.08) 60%, rgba(255,255,255,0) 100%);
  transform: translateX(-140%) skewX(-18deg);
  animation: reportScannerSweep ${REPORT_ANIMATION_TIMING.scannerSweepS}s cubic-bezier(.22, .61, .36, 1) forwards;
  mix-blend-mode: screen; z-index: 50;
}
.report-root.mode-colored .container { animation: reportReadyFlash ${REPORT_ANIMATION_TIMING.readyFlashS}s ease-out; }
@keyframes reportScannerSweep { from { transform: translateX(-140%) skewX(-18deg); } to { transform: translateX(140%) skewX(-18deg); } }
@keyframes reportReadyFlash { 0% { filter: brightness(1) saturate(1); } 20% { filter: brightness(1.35) saturate(1.45); } 100% { filter: brightness(1) saturate(1); } }
`;

type DonutSegment = { pct: number; neutral: string; final: string };
const DONUT_SEGMENTS: DonutSegment[] = [
  { pct: 44, neutral: '#7B6184', final: '#d8c8a1' },
  { pct: 25, neutral: '#957B9E', final: '#b8c0eb' },
  { pct: 16, neutral: '#AC98B3', final: '#9c82a6' },
  { pct: 10, neutral: '#C4B6C9', final: '#8d8ac3' },
  { pct: 5, neutral: '#DED6E0', final: '#89c4b3' },
];

const ICON_SIZE = 18;

export const ReportBuildSection = ({ className }: { className?: string }) => {
  useLocale();

  const rootRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!(rootRef.current && canvasRef.current)) return;
    const root = rootRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;

    let disposed = false;
    const timeouts = new Set<ReturnType<typeof setTimeout>>();
    const rafIds = new Set<number>();
    const delay = (cb: () => void, ms: number) => {
      const id = setTimeout(() => {
        timeouts.delete(id);
        if (!disposed) cb();
      }, ms);
      timeouts.add(id);
    };
    const raf = (cb: FrameRequestCallback) => {
      const id = requestAnimationFrame((t) => {
        rafIds.delete(id);
        if (!disposed) cb(t);
      });
      rafIds.add(id);
    };

    const donutState = { progress: 0, colorMix: 0, animated: false };

    function mixColor(a: string, b: string, t: number) {
      const pa = Number.parseInt(a.slice(1), 16);
      const pb = Number.parseInt(b.slice(1), 16);
      const ar = (pa >> 16) & 255;
      const ag = (pa >> 8) & 255;
      const ab = pa & 255;
      const br = (pb >> 16) & 255;
      const bg = (pb >> 8) & 255;
      const bb = pb & 255;
      const r = Math.round(ar + (br - ar) * t);
      const g = Math.round(ag + (bg - ag) * t);
      const bl = Math.round(ab + (bb - ab) * t);
      return `rgb(${r},${g},${bl})`;
    }

    function drawRiskDonut() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let remaining = donutState.progress;
      let start = -90;
      for (const seg of DONUT_SEGMENTS) {
        if (remaining <= 0) break;
        const segDeg = seg.pct * 3.6;
        const vis = Math.min(segDeg, remaining);
        ctx.beginPath();
        ctx.strokeStyle = mixColor(seg.neutral, seg.final, donutState.colorMix);
        ctx.lineWidth = 38;
        ctx.lineCap = 'butt';
        ctx.arc(90, 90, 71, (start * Math.PI) / 180, ((start + vis) * Math.PI) / 180);
        ctx.stroke();
        start += segDeg;
        remaining -= vis;
      }
    }

    function startDonutBuild() {
      if (donutState.animated) return;
      donutState.animated = true;
      const start = performance.now();
      const dur = REPORT_ANIMATION_TIMING.donutBuildDurationMs;
      function step(now: number) {
        const t = Math.min((now - start) / dur, 1);
        const eased = 1 - (1 - t) ** 3;
        donutState.progress = eased * 360;
        drawRiskDonut();
        if (t < 1) raf(step);
      }
      raf(step);
    }

    function colorizeDonut() {
      const start = performance.now();
      const dur = REPORT_ANIMATION_TIMING.donutColorizeDurationMs;
      function step(now: number) {
        const t = Math.min((now - start) / dur, 1);
        donutState.colorMix = t;
        drawRiskDonut();
        if (t < 1) raf(step);
      }
      raf(step);
    }

    function runSequence() {
      root.className = `${baseClass} mode-outline`;
      drawRiskDonut();

      delay(() => {
        root.className = `${baseClass} mode-overview`;
      }, REPORT_ANIMATION_TIMING.phaseOutlineToOverviewMs);

      delay(() => {
        root.className = `${baseClass} mode-nocolor ai-reveal`;
        startDonutBuild();
        for (const el of root.querySelectorAll<HTMLElement>('.type-title')) {
          el.style.visibility = 'visible';
        }
      }, REPORT_ANIMATION_TIMING.phaseOverviewToAiRevealMs);

      delay(() => {
        root.className = `${baseClass} mode-colored`;
        colorizeDonut();
      }, REPORT_ANIMATION_TIMING.phaseAiRevealToColoredMs);
    }

    const baseClass = 'report-root report-build-animation';

    // Trigger the build the first time the section scrolls into view.
    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started) {
            started = true;
            runSequence();
            observer.disconnect();
          }
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(root);

    return () => {
      disposed = true;
      observer.disconnect();
      for (const id of timeouts) clearTimeout(id);
      for (const id of rafIds) cancelAnimationFrame(id);
    };
  }, []);

  return (
    <section
      className={cn('report-root report-build-animation w-full select-none', className)}
      id="our-solution"
      ref={rootRef}
    >
      <style dangerouslySetInnerHTML={{ __html: SCOPED_CSS }} />
      <div className="container">
        <div className="hero">
          <h1 className="type-title">{t('landing.yourComplianceReportIsReady')}</h1>
        </div>

        <div className="tag" style={{ margin: '0 0 16px 0' }}>
          {t('landing.profileDecision')}{' '}
        </div>
        <div className="grid">
          <div className="card">
            <div className="profile-top">
              <div className="avatar">{t('landing.am')}</div>
              <div>
                <h2>{t('landing.alexMorgan')}</h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#777' }}>
                    {t('workspace.riskLevel')}
                  </span>
                  <span className="badge">{t('reports.medium')}</span>
                </div>
              </div>
            </div>
            <h3 className="section-sub-title">{t('reports.clientInformation')}</h3>
            <div className="info-row">
              <span> {t('copy.age')} </span>
              <span>{number(34)}</span>
            </div>
            <div className="info-row">
              <span> {t('copy.nationality')} </span>
              <span>{t('landing.canadian')}</span>
            </div>
            <div className="info-row">
              <span>{t('landing.netWorth')}</span>
              <span>
                {formatNumber(2400000, getLocale(), {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                })}
              </span>
            </div>
            <div className="info-row">
              <span> {t('copy.job')} </span>
              <span>{t('landing.softwareEngineer')}</span>
            </div>
          </div>

          <div className="card">
            <h2 className="type-title">{t('landing.riskSummary')}</h2>
            <p style={{ color: '#443649', lineHeight: '24px', fontSize: '14px' }}>
              {t('landing.basedOnTheConductedKycChecksTheClientPresents')}{' '}
            </p>
            <div className="pills">
              <div className="pill pill-pink">
                <span className="report-icon pill-icon">
                  <Ban size={ICON_SIZE} />
                </span>
                {t('landing.globalWatchlistMatch')}{' '}
              </div>
              <div className="pill pill-pink">
                <span className="report-icon pill-icon">
                  <ShieldAlert size={ICON_SIZE} />
                </span>
                {t('landing.pepDetected')}{' '}
              </div>
              <div className="pill pill-gold">
                <span className="report-icon pill-icon">
                  <Search size={ICON_SIZE} />
                </span>
                {t('landing.adverseMediaFound')}{' '}
              </div>
              <div className="pill pill-pink">
                <span className="report-icon pill-icon">
                  <Users size={ICON_SIZE} />
                </span>
                {t('landing.1HighRiskStakeholder')}{' '}
              </div>
              <div className="pill pill-gold">
                <span className="report-icon pill-icon">
                  <Users size={ICON_SIZE} />
                </span>
                {t('landing.3MediumRiskStakeholders')}{' '}
              </div>
            </div>
          </div>
        </div>

        <div className="tag" style={{ margin: '64px 0 16px 0' }}>
          {t('workspace.riskLevelJustification')}{' '}
        </div>
        <div className="just-grid">
          <div className="card">
            <h2 className="type-title">{t('workspace.breakdownOfRiskFactors')}</h2>
            <p style={{ color: '#443649' }}>
              {t('landing.relativeContributionOfEachFactorToTheClientS')}{' '}
            </p>
            <div className="donut-wrap">
              <div className="donut-canvas-wrap">
                <canvas height={180} id="riskDonutCanvas" ref={canvasRef} width={180} />
                <div className="donut-hole" />
              </div>
              <div className="legend">
                <div className="legend-row">
                  <div className="legend-label">
                    <span style={{ background: '#d8c8a1' }} />
                    {t('landing.occupationalRisk')}{' '}
                  </div>
                  <div className="legend-value">
                    {formatNumber(0.44, getLocale(), { style: 'percent' })}
                  </div>
                </div>
                <div className="legend-row">
                  <div className="legend-label">
                    <span style={{ background: '#b8c0eb' }} />
                    {t('landing.politicalExposure')}{' '}
                  </div>
                  <div className="legend-value">
                    {formatNumber(0.25, getLocale(), { style: 'percent' })}
                  </div>
                </div>
                <div className="legend-row">
                  <div className="legend-label">
                    <span style={{ background: '#9c82a6' }} />
                    {t('landing.adverseMedia')}{' '}
                  </div>
                  <div className="legend-value">
                    {formatNumber(0.16, getLocale(), { style: 'percent' })}
                  </div>
                </div>
                <div className="legend-row">
                  <div className="legend-label">
                    <span style={{ background: '#8d8ac3' }} />
                    {t('landing.sanctionsExposure')}{' '}
                  </div>
                  <div className="legend-value">
                    {formatNumber(0.1, getLocale(), { style: 'percent' })}
                  </div>
                </div>
                <div className="legend-row">
                  <div className="legend-label">
                    <span style={{ background: '#89c4b3' }} />
                    {t('landing.jurisdictionRisk')}{' '}
                  </div>
                  <div className="legend-value">
                    {formatNumber(0.05, getLocale(), { style: 'percent' })}
                  </div>
                </div>
              </div>
            </div>
            <div className="risk-caption">
              {t('landing.seeWhyTheBusinessHasBeenAssignedA')}{' '}
              <span className="risk-level-text">{t('reports.medium')}</span>{' '}
              {t('landing.riskLevel')}{' '}
            </div>
          </div>

          <div className="card">
            <h2 className="type-title">{t('landing.whyRiskLevelIsMedium')}</h2>
            <div className="bullet">
              {t('landing.riskIndicatorsArePresentButIndirectAndBusinessRelated')}{' '}
            </div>
            <div className="bullet">{t('landing.noCriticalSanctionsOrAdverseMediaFindings')}</div>
            <div className="bullet">{t('landing.jurisdictionAndOccupationalRisksAreModerate')}</div>
            <div className="bullet">{t('landing.overallExposureExceedsLowRiskThresholds')}</div>
          </div>
        </div>

        <div className="tag" style={{ margin: '64px 0 10px 0' }}>
          {t('landing.detectedSignals')}{' '}
        </div>
        <div className="section-title type-title" style={{ margin: '10px 0 24px 0' }}>
          {t('landing.riskSignals')}{' '}
        </div>
        <div className="signals">
          <div className="signal-card" style={{ borderColor: '#C6AC76', background: '#F7F4ED' }}>
            <div className="signal-header">
              <h4>{t('landing.globalWatchLists')}</h4>
              <span className="report-icon">
                <Ban size={ICON_SIZE} />
              </span>
            </div>
            <div className="signal-content">
              <div className="num">{number(2)}</div>
              <div className="desc">{t('landing.linkedToSanctionedCountriesOrEntities')}</div>
            </div>
            <div className="signal-footer">
              <div className="foot">{t('landing.2IssuesDetected')}</div>
              <div className="more">
                {t('landing.moreDetails')}{' '}
                <span className="report-icon">
                  <ChevronRight size={16} />
                </span>
              </div>
            </div>
          </div>

          <div className="signal-card" style={{ borderColor: '#d7a8b5', background: '#F5EFF1' }}>
            <div className="signal-header">
              <h4>{t('landing.pepScreening')}</h4>
              <span className="report-icon">
                <ShieldAlert size={ICON_SIZE} />
              </span>
            </div>
            <div className="signal-content">
              <div className="num">{number(3)}</div>
              <div className="desc">
                {t('landing.indirectRelationshipWithPoliticallyExposedPersons')}
              </div>
            </div>
            <div className="signal-footer">
              <div className="foot">{t('landing.3IssuesDetected')}</div>
              <div className="more">
                {t('landing.moreDetails')}{' '}
                <span className="report-icon">
                  <ChevronRight size={16} />
                </span>
              </div>
            </div>
          </div>

          <div className="signal-card">
            <div className="signal-header">
              <h4>{t('landing.adverseMedia')}</h4>
              <span className="report-icon">
                <Search size={ICON_SIZE} />
              </span>
            </div>
            <div className="signal-content">
              <div className="num">{number(0)}</div>
              <div className="desc">{t('landing.noIssuesDetected')}</div>
            </div>
            <div className="signal-footer">
              <div className="foot">{t('landing.0IssuesDetected')}</div>
              <div className="more">
                {t('landing.moreDetails')}{' '}
                <span className="report-icon">
                  <ChevronRight size={16} />
                </span>
              </div>
            </div>
          </div>

          <div className="signal-card">
            <div className="signal-header">
              <h4>{t('landing.jurisdictionRisk')}</h4>
              <span className="report-icon">
                <ShieldAlert size={ICON_SIZE} />
              </span>
            </div>
            <div className="signal-content">
              <div className="num">{number(1)}</div>
              <div className="desc">{t('landing.onePriorRegulatoryIncident')}</div>
            </div>
            <div className="signal-footer">
              <div className="foot">{t('landing.1IssueDetected')}</div>
              <div className="more">
                {t('landing.moreDetails')}{' '}
                <span className="report-icon">
                  <ChevronRight size={16} />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="section-title type-title" style={{ margin: '48px 0 24px 0' }}>
          {t('landing.socialSignals')}{' '}
        </div>
        <div className="card social-card">
          <div className="social-header">{t('landing.socialSignals')}</div>
          <div className="social-content">
            <div className="num">{number(6)}</div>
            <div className="desc">
              {t('landing.multipleRiskIndicatorsIdentifiedAcrossSocialMediaProfiles')}{' '}
            </div>
          </div>
          <div className="social-item">
            <h5>{t('landing.corporateAssociationVerifiedThroughLinkedinProfile')}</h5>
            <p>
              <span className="report-icon">
                <Linkedin size={ICON_SIZE} />
              </span>
              {t('landing.linkedinClientAccount')}{' '}
            </p>
          </div>
          <div className="social-item">
            <h5>{t('landing.frequentDisplayOfLuxuryAssetsInconsistentWithDeclaredProfile')}</h5>
            <p>
              <span className="report-icon">
                <Instagram size={ICON_SIZE} />
              </span>
              {t('landing.instagramClientAccount')}{' '}
            </p>
          </div>
          <div className="social-item" style={{ borderColor: '#d7a8b5', background: '#F5EFF1' }}>
            <h5>{t('landing.repeatedInteractionWithPoliticallySensitiveContent')}</h5>
            <p>
              <span className="report-icon">
                <XLogo size={ICON_SIZE} />
              </span>
              {t('landing.xClientAccount')}{' '}
            </p>
          </div>
          <div className="more">
            {t('landing.moreDetails')}{' '}
            <span className="report-icon">
              <ChevronRight size={16} />
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
